import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, apiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, Check } from "lucide-react";

const suggestions = ["Alongamento", "Manutenção", "Banho de gel", "Esmaltação", "Pé e mão"];

export default function Onboarding() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState("");
  const [studioName, setStudioName] = useState("");
  const [team, setTeam] = useState(false);
  const [avg, setAvg] = useState(60);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleService = (s) => setServices((cur) => cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding", {
        display_name: displayName || "Você",
        studio_name: studioName || "Meu Studio",
        works_with_team: team,
        avg_appointments_per_month: Number(avg) || 60,
        initial_services: services,
      });
      await refreshUser();
      toast.success("Tudo pronto! Bem-vinda ao NailControl.");
      nav("/app");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };

  const canNext =
    (step === 1 && displayName.trim().length > 0) ||
    (step === 2 && studioName.trim().length > 0) ||
    (step === 3) ||
    (step === 4 && Number(avg) > 0) ||
    (step === 5) ||
    (step === 6);

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="mb-6 flex items-center gap-2" data-testid="onboarding-progress">
          {[1,2,3,4,5,6].map(n => (
            <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-[#682F3E]" : "bg-[#EBD8D2]"}`} />
          ))}
        </div>
        <div className="card-nc p-6 md:p-8 space-y-6">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Como podemos chamar você?</h2>
              <input data-testid="onb-name" autoFocus value={displayName} onChange={e => setDisplayName(e.target.value)}
                placeholder="Seu primeiro nome"
                className="w-full h-12 px-3 rounded-xl border border-[#E4E4E7] focus:border-[#682F3E] focus:ring-2 focus:ring-[#682F3E]/10 outline-none" />
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Qual o nome do seu espaço?</h2>
              <p className="text-sm text-[#71717A]">Ex: Bella Nail Studio</p>
              <input data-testid="onb-studio" autoFocus value={studioName} onChange={e => setStudioName(e.target.value)}
                className="w-full h-12 px-3 rounded-xl border border-[#E4E4E7] focus:border-[#682F3E] focus:ring-2 focus:ring-[#682F3E]/10 outline-none" />
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Você trabalha:</h2>
              <div className="grid grid-cols-2 gap-3">
                {[{k:false,l:"Sozinha"},{k:true,l:"Com equipe"}].map(o => (
                  <button key={o.l} data-testid={`onb-team-${o.k}`} onClick={() => setTeam(o.k)}
                    className={`p-4 rounded-xl border-2 text-left transition ${team === o.k ? "border-[#682F3E] bg-[#F5ECE8]" : "border-[#E4E4E7]"}`}>
                    <div className="font-semibold text-[#27272A]">{o.l}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Quantos atendimentos você realiza por mês?</h2>
              <input data-testid="onb-avg" type="number" inputMode="numeric" value={avg} onChange={e => setAvg(e.target.value)}
                className="w-full h-12 px-3 rounded-xl border border-[#E4E4E7] focus:border-[#682F3E] focus:ring-2 focus:ring-[#682F3E]/10 outline-none" />
              <p className="text-sm text-[#71717A]">Usaremos essa informação para distribuir os custos do seu negócio durante a precificação.</p>
            </div>
          )}
          {step === 5 && (
            <div className="space-y-4">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Quais serviços você oferece?</h2>
              <p className="text-sm text-[#71717A]">Selecione para começar. Você pode editar depois.</p>
              <div className="grid grid-cols-2 gap-2">
                {suggestions.map(s => (
                  <button key={s} data-testid={`onb-svc-${s}`} onClick={() => toggleService(s)}
                    className={`p-3 rounded-xl border-2 text-left flex items-center justify-between transition ${services.includes(s) ? "border-[#682F3E] bg-[#F5ECE8]" : "border-[#E4E4E7]"}`}>
                    <span className="font-medium text-[#27272A]">{s}</span>
                    {services.includes(s) && <Check size={16} className="text-[#682F3E]" />}
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 6 && (
            <div className="text-center py-6 space-y-3">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#EBF2EE] flex items-center justify-center">
                <Check size={32} className="text-[#4F7A61]" />
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Seu NailControl está pronto.</h2>
              <p className="text-[#71717A]">Vamos começar organizando sua agenda e descobrindo seu preço ideal.</p>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <button data-testid="onb-back" onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="text-[#71717A] hover:text-[#27272A] flex items-center gap-1 text-sm font-medium disabled:opacity-30">
              <ChevronLeft size={16} /> Voltar
            </button>
            {step < 6 ? (
              <button data-testid="onb-next" disabled={!canNext} onClick={() => setStep(s => s + 1)}
                className="btn-primary-nc flex items-center gap-1">
                Avançar <ChevronRight size={16} />
              </button>
            ) : (
              <button data-testid="onb-finish" onClick={finish} disabled={loading} className="btn-primary-nc">
                {loading ? "Salvando…" : "Ir para meu painel"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
